import { useCallback, useEffect } from 'react';
import { UseNavigateResult } from '@tanstack/react-router';
import { Authn, HandleCallbackProps } from '@frontend/auth';
import { SignInMethod } from '@frontend/auth-helpers';
import appConfig from '@frontend/env';
import { useTranslation } from '@frontend/i18n';
import { hydraShellService } from '../services';
import { getRedirectURL } from '../services/fetch-wrapper.ts';
import { handlePostAuthRedirect } from '../services/route-utils';
import { ContentLoader } from './content-loader.tsx';
import { FormWrap } from './form-wrap';

interface Props<T extends string> {
  auth: Authn;
  navigate: UseNavigateResult<T>;
  signInMethod: SignInMethod;
  getAuthCallbackProps?: () => HandleCallbackProps | undefined;
}

export const SignInCallback = <T extends string>({ auth, navigate, signInMethod, getAuthCallbackProps }: Props<T>) => {
  const { t } = useTranslation('SignIn');

  const handleCallback = useCallback(async () => {
    let redirectURL = appConfig.DEFAULT_REDIRECT;
    try {
      const initialLoginResponse = await hydraShellService.initializeOAuth2Flow();
      redirectURL = !!initialLoginResponse.requestUrl
        ? getRedirectURL(initialLoginResponse.requestUrl) ?? appConfig.DEFAULT_REDIRECT
        : appConfig.DEFAULT_REDIRECT;
    } catch (error) {
      console.error(error);
    }

    // Always exchange the code and accept the hydra OAuth2 flow, even if the browser
    // already has an authenticated session. In multi-user shared-browser scenarios
    // (e.g. user A logs out of the desktop app but their web session is still live,
    // then user B signs in from the desktop) skipping these steps means we never call
    // acceptOAuth2Flow and never get the correct redirectTo (the desktop's weave://...
    // callback). The caller of this page should always carry a fresh OAuth code.
    auth.changeAuthMethod(signInMethod);

    let callbackProps: HandleCallbackProps | undefined = undefined;
    if (getAuthCallbackProps) {
      callbackProps = getAuthCallbackProps();
    }

    try {
      await auth.handleCallback(callbackProps);
      const acceptLoginResponse = await hydraShellService.acceptOAuth2Flow();
      handlePostAuthRedirect(acceptLoginResponse.redirectTo || appConfig.DEFAULT_REDIRECT, navigate, {
        reason: `acceptLoginResponse for ${signInMethod}`,
      });
    } catch (e: any) {
      console.error(e);
      handlePostAuthRedirect(redirectURL || appConfig.DEFAULT_REDIRECT, navigate, {
        reason: `error caught in sign-in callback ${signInMethod}`,
        error: e,
      });
    }
  }, [auth, getAuthCallbackProps, navigate, signInMethod]);

  useEffect(() => {
    handleCallback();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <FormWrap>
      <ContentLoader message={t('Logging in...')} show />
    </FormWrap>
  );
};
